import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import {
  checkWheelFitment,
  plateInfoCarName,
  plateInfoModelName,
  tpmsRequirementText,
} from '../../../util/wheelFitment';
import { carFitment, carSearchParams } from '../../../util/carSearchParams';
import { servicesForListing } from '../../../util/serviceTargetingCore';
import {
  SERIES_STORAGE_KEY,
  carIdOf,
  carLabel,
  formatReg,
  isCarPayload,
  normalizeReg,
  selectCar,
  useCurrentCar,
  useRecentCars,
} from '../../../util/savedCar';
import { trackListingEvent } from '../../../util/ecommerceEvents';
import { RegNumInput } from '../../WP/RegNumInput';

import css from './BuyBoxFitCheck.module.css';

const readSeries = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(SERIES_STORAGE_KEY) : null;
  } catch {
    return null;
  }
};

/* The product type the "wheels that fit" link searches for. Company listing
 * types carry their own names; `businesswheel` (no dash) is RIMS. */
const searchAdTypeOf = publicData => {
  const raw = publicData?.adType || publicData?.listingType || '';
  if (['tire', 'businesstire'].includes(raw)) return 'tire';
  if (['rim', 'businesswheel'].includes(raw)) return 'rim';
  return 'wheel';
};

const verdictOf = result =>
  result?.compatible === true ? 'fits' : result?.compatible === false ? 'no_fit' : 'uncertain';

/**
 * The fit check at the top of the buy box (P25009-482 BR-3 / BR-4).
 *
 *   no car     plate input + "Kolla"
 *   fits       green "✓ Passar din {bil}"
 *   no fit     red "Passar inte din {bil}" + a link to wheels that do fit
 *   uncertain  the listing lacks a fact the check needs — said plainly
 *
 * The car comes from useCurrentCar, so a plate typed on any page shows here
 * without being typed again. "Byt bil" lists the logged-in buyer's saved cars
 * first, then the last three plates this browser looked up.
 */
const BuyBoxFitCheck = ({ listing }) => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const { plateInfo: storePlateInfo, reg, loading } = useCurrentCar();
  /* Only a payload that describes a car counts. RegNumInput puts its
   * half-way answers ("choose a model" / "choose a series") into Redux too,
   * and treating one of those as a car swapped the input away while its
   * picker window was still open. */
  const plateInfo = isCarPayload(storePlateInfo) ? storePlateInfo : null;
  const recents = useRecentCars();
  const userId = useSelector(state => state.user?.currentUser?.id?.uuid);
  const savedQueries = useSelector(state => state.ListingPage?.savedQueries) || [];
  const userSeries = useSelector(
    state => state.user?.currentUser?.attributes?.profile?.publicData?.regSeries
  );

  const [changing, setChanging] = useState(false);
  const [inputReg, setInputReg] = useState('');
  const [picking, setPicking] = useState(null);

  const publicData = listing?.attributes?.publicData || {};
  const listingId = listing?.id?.uuid;

  const result = plateInfo
    ? checkWheelFitment({
        listingPublicData: publicData,
        licensePlateInfo: plateInfo,
        hasSeries: !!(userSeries || readSeries()),
      })
    : null;
  /* A car chosen by model on the search page has no plate (P25009-491) and is
   * named by its version: "Volkswagen ID.4 (E2) 2020–". */
  const carName = (plateInfo && carLabel(plateInfo, reg)) || plateInfoCarName(plateInfo) || 'din bil';
  const modelName = plateInfoModelName(plateInfo) || carName;

  /* A new car closes "Byt bil" — whether it came from a chip or the input. */
  const carKey = plateInfo ? `${reg}|${carName}` : null;
  const previousCarKey = useRef(carKey);
  useEffect(() => {
    if (carKey && previousCarKey.current !== carKey) {
      setChanging(false);
      setInputReg('');
    }
    previousCarKey.current = carKey;
  }, [carKey]);

  /* BR-7 — fit_check counts the results a buyer asked for (a plate typed
   * here, a chip picked), not the car already remembered when the page
   * opened. */
  const trackFitCheck = payload => {
    if (!payload) return;
    const checked = checkWheelFitment({ listingPublicData: publicData, licensePlateInfo: payload });
    trackListingEvent('fit_check', {
      listing_id: listingId,
      result: verdictOf(checked),
      surface: 'buy_box',
    });
  };

  const hubHoleOptions =
    (config?.listing?.listingFields || []).find(f => f.key === 'hubHole')?.enumOptions || [];
  const fittingSearch = () => {
    const params = new URLSearchParams(
      carSearchParams({ data: plateInfo, adType: searchAdTypeOf(publicData), hubHoleOptions })
    );
    if (reg) params.set('regNumber', normalizeReg(reg));
    else if (carIdOf(plateInfo)) params.set('carId', carIdOf(plateInfo));
    return `/annonser?${params.toString()}`;
  };

  /* Saved cars (logged in) first, then this browser's recent plates; one chip
   * per plate, never the car already on screen. */
  const currentReg = normalizeReg(reg);
  const seen = new Set(currentReg ? [currentReg] : []);
  const chips = [
    ...savedQueries
      .filter(q => q?.savedQuery?.regNumber)
      .map(q => ({ reg: q.savedQuery.regNumber, name: q.savedQuery.title || '', saved: true })),
    ...recents.map(r => ({ reg: r.reg, series: r.series, name: r.label || r.name, data: r.data })),
  ].filter(chip => {
    const key = normalizeReg(chip.reg);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const pickChip = chip => {
    setPicking(normalizeReg(chip.reg));
    selectCar(chip, { dispatch, userId })
      .then(trackFitCheck)
      .catch(() => null)
      .finally(() => setPicking(null));
  };

  const plateInput = (
    <RegNumInput
      size="inline"
      page="listingPage"
      submitLabel="Kolla"
      savedRegNum={inputReg}
      changeRegNum={setInputReg}
      // The new car itself reaches this component through Redux.
      updateState={trackFitCheck}
    />
  );

  const chipRow =
    chips.length > 0 ? (
      <div className={css.chips} role="list" aria-label="Senaste bilar">
        {chips.map(chip => (
          <button
            key={normalizeReg(chip.reg)}
            type="button"
            role="listitem"
            className={css.chip}
            onClick={() => pickChip(chip)}
            disabled={!!picking}
          >
            <span className={css.chipReg}>{formatReg(chip.reg)}</span>
            {chip.name ? <span className={css.chipName}>{chip.name}</span> : null}
          </button>
        ))}
      </div>
    ) : null;

  /* The plate input stays MOUNTED for the whole life of the box and is only
   * hidden while a result shows. RegNumInput owns the "Välj motor" / "Välj
   * Modell" window and finishes its own click handler after the car reaches
   * Redux; unmounting it at that moment tore the window's portal down under
   * it ("Failed to execute 'removeChild' on 'Node'"). */
  const showPicker = !plateInfo || changing;
  const isLoading = loading && !changing;
  const verdict = verdictOf(result);

  /* P25009-486 BR-4 — a wheel whose centre bore is wider than the car's hub
   * still fits, with centring rings; say so, and where to get them. A bore
   * NARROWER than the hub is a "Passar inte" the fit check already gives. */
  const listingBore = parseFloat(String(publicData.hubHole || '').replace(/[,_]/g, '.'));
  const carBore = plateInfo ? carFitment(plateInfo).centreBore : null;
  const needsRings =
    verdict !== 'no_fit' &&
    Number.isFinite(listingBore) &&
    Number.isFinite(carBore) &&
    listingBore > carBore + 0.1;
  const sellerServices = servicesForListing(
    listing?.author?.attributes?.profile?.publicData?.services,
    publicData
  );
  const sellerHasRings = sellerServices.some(service => service.templateKey === 'centreringsringar');
  const mm = value => `${String(Math.round(value * 10) / 10).replace('.', ',')} mm`;

  /* P25009-489 BR-5 — "TPMS krävs" + the sensor model, from the car database.
   * Only where a sensor goes in the wheel (complete wheels and rims), and not
   * under "Passar inte". What follows says where the sensors come from: the
   * seller's own answer on the listing, else the seller's TPMS service. */
  const tpmsText =
    plateInfo && verdict !== 'no_fit' && searchAdTypeOf(publicData) !== 'tire'
      ? tpmsRequirementText(plateInfo)
      : '';
  const tpmsSource =
    publicData.tpms === true
      ? 'Säljaren anger att hjulen har sensorer.'
      : sellerServices.some(service => service.templateKey === 'tpms')
      ? 'Kan läggas till i kassan.'
      : 'Fråga säljaren om sensorer.';

  return (
    <div className={css.root}>
      {showPicker ? null : (
        <>
          <div
            className={classNames(css.result, {
              [css.resultFits]: verdict === 'fits',
              [css.resultNoFit]: verdict === 'no_fit',
              [css.resultUncertain]: verdict === 'uncertain',
            })}
            role="status"
          >
            <span className={css.resultIcon} aria-hidden="true">
              {verdict === 'fits' ? '✓' : verdict === 'no_fit' ? '✕' : '?'}
            </span>
            <span className={css.resultText}>
              {verdict === 'fits' ? (
                <>
                  Passar din <strong>{carName}</strong>
                </>
              ) : verdict === 'no_fit' ? (
                <>
                  Passar inte din <strong>{carName}</strong>
                </>
              ) : (
                <>
                  Vi kan inte bekräfta att den passar din <strong>{carName}</strong>
                </>
              )}
              {reg ? <span className={css.resultReg}> · {formatReg(reg)}</span> : null}
            </span>
            <button type="button" className={css.textButton} onClick={() => setChanging(true)}>
              Byt bil
            </button>
          </div>
          {verdict === 'no_fit' ? (
            <a className={css.fittingLink} href={fittingSearch()}>
              Visa hjul som passar din {modelName} →
            </a>
          ) : null}
          {needsRings ? (
            <p className={css.ringsNote}>
              Navhål {mm(listingBore)} → bilens {mm(carBore)}: centreringsringar behövs.{' '}
              {sellerHasRings ? 'Kan läggas till i kassan.' : 'Fråga säljaren om ringar.'}
            </p>
          ) : null}
          {tpmsText ? (
            <p className={css.ringsNote} data-testid="tpms-note">
              {tpmsText}. {tpmsSource}
            </p>
          ) : null}
        </>
      )}

      <div className={showPicker ? css.picker : css.hidden}>
        <div className={css.promptRow}>
          <span className={css.prompt}>
            {changing ? 'Välj en annan bil' : 'Passar den din bil?'}
          </span>
          {changing ? (
            <button type="button" className={css.textButton} onClick={() => setChanging(false)}>
              Avbryt
            </button>
          ) : null}
        </div>
        {isLoading ? <p className={css.muted}>Hämtar din bil…</p> : null}
        <div className={isLoading ? css.hidden : css.picker}>
          {plateInput}
          {chipRow}
        </div>
      </div>
    </div>
  );
};

export default BuyBoxFitCheck;
